import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API } from "../contexts/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Plus, Pencil, Trash2, Megaphone } from "lucide-react";
import { toast } from "sonner";

export default function CommunityNoticesPage() {
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", content: "", date: "" });

  const load = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/community/notices`); setNotices(data); }
    catch (e) { if (e.response?.status !== 401) toast.error("Error cargando"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm({ title: "", content: "", date: new Date().toISOString().split('T')[0] }); setDialogOpen(true); };
  const openEdit = (n) => { setEditing(n); setForm({ title: n.title, content: n.content, date: n.date }); setDialogOpen(true); };

  const handleSave = async () => {
    try {
      if (editing) { await axios.put(`${API}/community/notices/${editing.id}`, form); toast.success("Circular actualizada"); }
      else { await axios.post(`${API}/community/notices`, form); toast.success("Circular creada"); }
      setDialogOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar esta circular?")) return;
    try { await axios.delete(`${API}/community/notices/${id}`); toast.success("Eliminada"); load(); }
    catch { toast.error("Error"); }
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-400">Cargando...</div>;

  return (
    <div data-testid="community-notices-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-2xl sm:text-3xl tracking-tight font-bold text-gray-900" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Circulares</h2>
          <p className="text-sm text-gray-500 mt-1">{notices.length} circulares</p>
        </div>
        <button onClick={openNew} className="bg-[#002FA7] text-white hover:bg-[#00227B] rounded-sm px-4 py-2 text-sm font-semibold transition-all flex items-center gap-2" data-testid="add-notice-button">
          <Plus className="w-4 h-4" /> Nueva circular
        </button>
      </div>

      <div className="space-y-4">
        {notices.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-sm p-8 text-center text-gray-400">No hay circulares</div>
        ) : notices.map(n => (
          <div key={n.id} className="bg-white border border-gray-200 rounded-sm p-5 hover:-translate-y-0.5 hover:shadow-sm transition-all">
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center gap-2">
                <Megaphone className="w-4 h-4 text-[#002FA7]" />
                <h3 className="text-sm font-bold text-gray-900">{n.title}</h3>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-xs text-gray-400 mr-2">{n.date}</span>
                <button onClick={() => openEdit(n)} className="p-1 text-gray-400 hover:text-[#002FA7]"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => handleDelete(n.id)} className="p-1 text-gray-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{n.content}</p>
          </div>
        ))}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-sm" data-testid="notice-dialog">
          <DialogHeader><DialogTitle>{editing ? "Editar circular" : "Nueva circular"}</DialogTitle><DialogDescription>Aviso para los propietarios</DialogDescription></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label className="text-sm font-semibold text-gray-900 mb-1 block">Titulo</Label><Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="rounded-sm" data-testid="notice-title-input" /></div>
            <div><Label className="text-sm font-semibold text-gray-900 mb-1 block">Fecha</Label><Input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} className="rounded-sm" /></div>
            <div><Label className="text-sm font-semibold text-gray-900 mb-1 block">Contenido</Label>
              <textarea value={form.content} onChange={e => setForm({...form, content: e.target.value})} rows={5} placeholder="Texto de la circular..." className="w-full border border-gray-200 rounded-sm p-3 text-sm focus:ring-2 focus:ring-[#002FA7] focus:border-transparent transition-all resize-none" data-testid="notice-content-input" />
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setDialogOpen(false)} className="bg-white text-gray-900 border border-gray-200 hover:bg-gray-50 rounded-sm px-4 py-2 text-sm font-semibold transition-all">Cancelar</button>
            <button onClick={handleSave} className="bg-[#002FA7] text-white hover:bg-[#00227B] rounded-sm px-4 py-2 text-sm font-semibold transition-all" data-testid="notice-save-button">Guardar</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
